import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { RoutingConfig } from '@app/routing/routing.config';
import { DevicesClass, DeviceTypes } from '@app/core/classes';
import { BreadcrumbsDataModel } from '@app/core/models/breadcrumbs';
import { NewsService } from '@app/modules/news-modules/news.service';
import { IBlogPostNewsDetail } from '@app/modules/news-modules/news.models';
import { NewsViewTypes } from '@app/shared/news-item/news-item.component';
import { PaginationService, RolesService, TokenService } from '@app/core/services';
import { FavoritesService } from '@app/modules/favorites-modules/favorites.service';
import { NewsStoryService } from '@app/modules/news-modules/news.story';
import { delay } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MenuItem } from 'primeng/api';
import { AccountEventBus, AccountStoryService } from '@app/modules/account-modules/account.story';
import { IAccountUserInfo } from '@app/modules/account-modules/account.model';

@UntilDestroy()
@Component({
  selector: 'app-news-list',
  templateUrl: 'news-lists.component.html',
  styleUrls: [`./news-lists.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class NewsListsComponent extends DevicesClass implements OnInit, OnDestroy {
  public NVType = NewsViewTypes;
  public BreadcrumbsData: BreadcrumbsDataModel = [
    {
      title: 'Новсти',
      url: '/' + RoutingConfig.News,
      active: true
    }
  ];

  public NewsListData: Array<IBlogPostNewsDetail> = [];
  public NewsListAuth: IAccountUserInfo = null;
  public NewsListMenu: MenuItem[] = this.onNewsListMenu();

  constructor(injector: Injector,
    public cdr: ChangeDetectorRef,
    public pagination: PaginationService,
    private route: ActivatedRoute,
    private router: Router,
    private accountStory: AccountStoryService,
    private tokenService: TokenService,
    private newsService: NewsService,
    private favoritesService: FavoritesService,
    private newsStoryService: NewsStoryService,
    private rolesService: RolesService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.onPaginationInit();
    this.onGetNewsApi(this.pagination.getPaginationIniURL(), true);
    this.onAccountStoryBus();
  }

  ngOnDestroy(): void {
    this.pagination.offPaginationUpdate();
  }

  onAccountStoryBus(): void {
    this.accountStory.AccountEventBus$.pipe(untilDestroyed(this)).subscribe(
      emit => this.onAccountStoryListener(emit)
    );
  }

  onAccountStoryListener(emit: AccountEventBus): void {
    if (emit.event === 'onAccountUpdate') {
      this.NewsListAuth = emit.data;
    }

    if (emit.event === 'onAccountLogout' || emit.event === 'onAccountEmpty') {
      this.NewsListAuth = null;
    }

    this.cdr.markForCheck();
  }

  onGetFavoritesNews(): void {
    if (!this.tokenService.accessToken) {
      return;
    }
    this.favoritesService.newsFavorites().subscribe(
      response => {
        this.newsStoryService.NewsEventBus.next({
          event: 'onUpdateFavorites',
          data: response.items
        })
      }
    );
  }

  onPaginationInit(): void {
    this.pagination.setPaginationURL(RoutingConfig.News);

    this.pagination.setPaginationValues({
      ini: this.pagination.getPaginationIniURL(),
      size: 10,
      selector: 'shared-news-item'
    });

    this.pagination.onPaginationUpdate().pipe(
      untilDestroyed(this)
    ).subscribe(
      value => !!value ? this.onGetNewsApi(value) : null
    );
  }

  onGetNewsApi(page: number = 1, pageLoad?: boolean): void {
    this.pagination.setPaginationValues({
      loading: !pageLoad
    });
    this.cdr.detectChanges();

    this.newsService.getAllNews(this.pagination.getPaginationValue().size, page)
      .pipe(
        delay(page === 1 || pageLoad ? 0 : 1000)
      ).subscribe(
      response => {
        this.NewsListData.push(...response.items);

        /**
         * Set pagination params.
         */
        this.pagination.setPaginationValues({
          index: response.pageIndex,
          pages: response.totalPages,
          count: response.totalCount,
          loading: false
        });
        this.pagination.onSetParamsURL('page', response.pageIndex);

        /**
         * Rewrite view.
         */
        this.cdr.detectChanges();

        /**
         * Set pagination events handlers.
         */
        if (pageLoad) {
          this.pagination.setPaginationEvents();
        }

        /**
         * Get of favorites news
         */
        this.onGetFavoritesNews();
      }
    )
  }

  onPageChange($event: any): void {
    this.pagination.onSetPagerURL($event);
  }

  onNewsListMenu(): Array<MenuItem> {
    return [{
      label: 'Управление новостями',
      items: [
        {
          label: 'Создать новость',
          icon: 'pi pi-plus-circle',
          routerLink: `/${RoutingConfig.News}/${RoutingConfig.NewsCreate}`
        }
      ]
    }]
  }

  get onAdminRole(): boolean {
    return this.rolesService.onAdminRole(this.NewsListAuth) || false;
  }

  get onModeratorRole(): boolean {
    return this.rolesService.onModeratorRole(this.NewsListAuth) || false;
  }
}
